// W2-3 — WHEN THE TURN NOTICE SHOWS. Owner ruling, 2026-07-29.
//
// HER RULING: "The cap surfaces only in use, warmly. At 5 remaining: a gentle notice inside/above the
// text box, in voice, no em dashes, ONCE PER DAY not per message. At the cap: the warm closing notice
// plus, where a higher tier exists, one soft upgrade line with the Upgrade pointer."
//
// THE SPLIT, AND IT IS THE WHOLE DESIGN OF THIS MODULE. `server/lala-cap.js` owns WHAT the notice says
// (it already owned the cap sentence, and a second copy of cap wording on the client would be a law
// with two answer keys). This owns WHEN it is shown, which the server cannot know: "once per day"
// means once per day ON HER SCREEN, across the drawer and the Ask Lala page, surviving a reload. So
// the finished sentence arrives on the wire and nothing here composes a word of it.
//
// It is in `src/lib` for the standing reason: a law inside App.tsx cannot be imported by a `.mjs`
// gate. This imports NOTHING, which is the entry requirement, and `scripts/w2-turn-notice.mjs` RUNS
// it — including the once-per-day memory, through an injected store rather than a real browser.

// Exactly what the server puts on the wire. Every field optional: an older client, a cached response
// or a surface that never asked must degrade to "no notice", never to a crash or a guess.
export type TurnPayload = {
  uncapped?: boolean;
  capped?: boolean;
  cap?: number;
  remaining?: number;
  tier?: string;
  localDay?: string | null;
  resetsAt?: string | null;
  notice?: string;
};

export type TurnNotice = { kind: "warning" | "capped"; text: string } | null;

// The one key, and it holds a DAY rather than a boolean. A boolean would need clearing at midnight by
// something, and nothing runs at midnight on a closed tab. A day string compares itself.
export const TURN_NOTICE_KEY = "lala-turn-notice-day";

// The smallest store this needs, so a gate can hand it a Map and a browser can hand it localStorage.
export type NoticeStore = { get(key: string): string | null; set(key: string, value: string): void };

// A member with private browsing on, or storage full, still gets her chat. She just gets the notice
// once per session instead of once per day, which is the right way for this to fail.
//
// >>> **THIS FILE MAY NOT IMPORT THE `safe-storage` DOOR, AND THE GATES SAID SO WITHIN A MINUTE OF
// TRYING (TIKTOK-WEBVIEW-1).** <<< `src/lib/*.ts` pure laws are LOADED BY `.mjs` FIXTURES through a
// `data:` URL, where a relative specifier cannot resolve — W2-3 and W6 both went red with
// `Failed to resolve module specifier "./safe-storage"`. The rule in CLAUDE.md is real and this is
// what enforces it.
//
// **SO THE PROTECTION IS INLINE HERE, AND IT IS THE SAME PROTECTION, IN THE ONE PLACE IT MUST BE:
// INSIDE THE `try`, NOT IN AN ARGUMENT.** The whole defect being fixed was a `try` in the callee
// with the throw in the caller; `window.localStorage` is dereferenced below the `try` keyword, so a
// raising getter is caught here exactly as the door catches it there. **`safe-storage-1.mjs`'s sweep
// excludes this file BY NAME AND SAYS WHY**, rather than the sweep quietly not covering it.
export function webStore(): NoticeStore {
  return {
    get(key) { try { return window.localStorage.getItem(key); } catch { return null; } },
    set(key, value) { try { window.localStorage.setItem(key, value); } catch { /* nothing to do */ } },
  };
}

// THE DECISION. `seenDay` is the local day the warning was last shown to her.
//
// The cap notice is NOT once-a-day: it is the standing state of the box, and hiding it would leave a
// member typing into a chat that has stopped answering with no explanation on screen.
export function turnNoticeFor(turns?: TurnPayload | null, seenDay?: string | null): TurnNotice {
  if (!turns || turns.uncapped) return null;
  const text = String(turns.notice || "").trim();
  if (!text) return null;
  if (turns.capped) return { kind: "capped", text };
  // No day on the payload means the once-per-day promise cannot be kept honestly, and showing a
  // gentle notice twice is a far smaller wrong than never showing it at all.
  if (turns.localDay && seenDay === turns.localDay) return null;
  return { kind: "warning", text };
}

// W6-2 — WHAT THE COMPOSER DOES AT THE CAP. Her ruling: "the input can disable with a quiet inline note
// until midnight; no modal."
//
// It lives here rather than at the two call sites for the reason this whole module exists: the drawer and
// the page each held their own copy of `notice?.kind === "capped"` AND their own copy of the placeholder
// string, which is two surfaces holding one law twice, and the placeholder is member-facing WORDING. The
// cap's words have exactly one home (server/lala-cap.js for the sentence, this for the six words in the
// empty box), and the two surfaces now read the same answer.
export const CAPPED_PLACEHOLDER = "Back at midnight your time";

// FUNNEL-4 — the wall closes the box too, and it is a THIRD state rather than a kinder cap (R18).
// The cap says *come back at midnight*, which is TRUE for a member with a daily allowance and a LIE
// for one whose five turns with Lala are spent: nothing comes back for her at midnight, and telling
// her it does is the house promising something it will not do. Her wall says what is really true and
// the control beside it is the way through. `atWall` DEFAULTS TO FALSE, so every existing caller is
// byte-identical (ITEM 12's neutrality argument).
export const WALL_PLACEHOLDER = "Come all the way in to keep talking";

export function composerLock(notice: TurnNotice, placeholder: string, atWall = false): { disabled: boolean; placeholder: string } {
  if (atWall) return { disabled: true, placeholder: WALL_PLACEHOLDER };
  const capped = notice?.kind === "capped";
  return { disabled: capped, placeholder: capped ? CAPPED_PLACEHOLDER : placeholder };
}

// The whole thing a call site does: read the memory, decide, write the memory back. One line in the
// chat handler, and the memory write is here rather than in App.tsx so both surfaces cannot drift.
// R98: the parameter is `unknown` because what arrives here comes off the WIRE, and every call site
// was asserting a shape it had not checked. The narrowing is PRESENCE (R53) and lives once, here,
// rather than as a cast at each surface.
export function applyTurnPayload(turns?: unknown, store?: NoticeStore): TurnNotice {
  const s = store || webStore();
  const payload = turns && typeof turns === "object" ? (turns as TurnPayload) : null;
  const notice = turnNoticeFor(payload, s.get(TURN_NOTICE_KEY));
  if (notice && notice.kind === "warning" && payload?.localDay) s.set(TURN_NOTICE_KEY, payload.localDay);
  return notice;
}
